import { cn } from "@/lib/utils";
import { Eye, EyeOff, Search } from "lucide-react";
import {
  ChangeEvent,
  type ComponentProps,
  type ReactNode,
  type Ref,
  useState,
} from "react";
import { useIMask } from "react-imask";
import { NumericFormat } from "react-number-format";
import { useFormField } from "../../../hooks/useFormField";

interface InputProps extends ComponentProps<"input"> {
  suffix?: ReactNode;
}

const inputBaseClassName =
  "peer border-input shadow-custom-input text-base md:text-sm w-full file:text-foreground placeholder:text-input-placeholder selection:bg-primary selection:text-primary-foreground aria-invalid:outline-destructive/60 aria-invalid:ring-destructive/20 dark:aria-invalid:outline-destructive dark:aria-invalid:ring-destructive/50 ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 aria-invalid:border-destructive/60 dark:aria-invalid:border-destructive flex min-w-0 rounded-md border bg-background min-h-10 text-foreground px-4 py-2 transition-[color,border-color,box-shadow] file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-md file:font-medium focus-visible:border-primary focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:focus-visible:ring-2 aria-invalid:focus-visible:outline-none dark:aria-invalid:focus-visible:ring-4";

export function CurrencyInput({
  className,
  ...props
}: ComponentProps<typeof NumericFormat>) {
  return (
    <NumericFormat
      displayType="input"
      thousandSeparator
      decimalScale={0}
      className={cn(inputBaseClassName, className)}
      {...props}
    />
  );
}

const IBAN_MASK = "XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX";
export function IBANInput({ ...props }: ComponentProps<typeof Input>) {
  const { ref, value, setValue } = useIMask(
    {
      mask: IBAN_MASK,
      definitions: {
        X: /[0-9a-zA-Z]/,
      },
      prepare: (value) => value.toUpperCase(),
    },
    {
      defaultValue: props.value ? props.value.toString() : "",
      onAccept: (value) => {
        props.onChange?.({
          target: {
            value: value.replace(/ /g, ""),
          },
        } as ChangeEvent<HTMLInputElement>);
      },
    },
  );

  return (
    <Input
      {...props}
      ref={ref as Ref<HTMLInputElement>}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export function Input({ className, type, suffix, ...props }: InputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const inputType = showPassword ? "text" : type;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="relative">
        <input
          type={inputType}
          data-slot="input"
          className={cn(
            inputBaseClassName,
            type === "number" &&
              "[--moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            type === "date" && "block",
            type === "search" && "ps-9",
            props.readOnly &&
              "bg-gray-50 border-gray-200 text-gray-900 cursor-default",
            className,
          )}
          {...props}
        />
        {type === "password" && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className="absolute right-4 top-1/2 -translate-y-1/2"
          >
            {showPassword ? (
              <Eye size={16} color="currentColor" />
            ) : (
              <EyeOff size={16} color="currentColor" />
            )}
          </button>
        )}

        {type === "search" && (
          <Search
            size={16}
            className="transition-opacity absolute left-3 top-1/2 -translate-y-1/2 opacity-50 peer-focus:opacity-100 pointer-events-none"
            strokeWidth={1.75}
          />
        )}

        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

export function FormInput(props: InputProps) {
  const { error } = useFormField();

  return <Input aria-invalid={!!error} {...props} />;
}
